import type { IUserData } from "../shared/session/interfaces/session";
import type { IEstimateOption } from "../services/api";

/**
 * Catalog funding methods (UPPER_SNAKE). Mirrors the canonical list in
 * `raas-widgets-backend/src/catalog/constants/catalog.constants.ts`.
 */
export const FUNDING_METHODS = [
  "DEBIT_CARD",
  "CREDIT_CARD",
  "ACH",
  "RTP",
  "WALLET",
  "CASH_DEPOSIT",
  "BANK_BALANCE",
] as const;
export type FundingMethod = (typeof FUNDING_METHODS)[number];

/**
 * Maps catalog funding method codes to the UI category used by the
 * payment-method selector: CARD | ACCOUNT | CASH | OTHER.
 */
export type FundingCategory = "CARD" | "ACCOUNT" | "CASH" | "OTHER";

export const FUNDING_METHOD_CATEGORY: Record<FundingMethod, FundingCategory> = {
  DEBIT_CARD: "CARD",
  CREDIT_CARD: "CARD",
  ACH: "ACCOUNT",
  RTP: "ACCOUNT",
  BANK_BALANCE: "ACCOUNT",
  WALLET: "OTHER",
  CASH_DEPOSIT: "CASH",
};

/**
 * Classify any funding method string into a UI category. Accepts both the
 * canonical catalog vocabulary and the legacy lowercase trio
 * (`card` / `bank_account` / `cash`).
 */
export function categoryFromFundingMethod(
  method: string | undefined | null
): FundingCategory | undefined {
  if (!method) return undefined;
  const upper = method.toUpperCase();
  if (upper in FUNDING_METHOD_CATEGORY) {
    return FUNDING_METHOD_CATEGORY[upper as FundingMethod];
  }
  switch (method) {
    case "card":
      return "CARD";
    case "bank_account":
      return "ACCOUNT";
    case "cash":
      return "CASH";
    default:
      return undefined;
  }
}

/**
 * Maps the widget's payment type (`CARD` | `BANK` | `CASH`) to a UI funding
 * category, so a selected payment method can be matched against a pre-quote SKU.
 */
export function categoryFromPaymentType(
  type: string | undefined | null
): FundingCategory | undefined {
  switch (type) {
    case "CARD":
      return "CARD";
    case "BANK":
      return "ACCOUNT";
    case "CASH":
      return "CASH";
    default:
      return undefined;
  }
}

/**
 * Resolves the canonical funding method (UPPER_SNAKE) pre-filled on the
 * deferred-payout-origination link for the sender.
 */
export function resolveFundingMethod(
  userData?: IUserData | null
): string | undefined {
  return userData?.paymentMethod?.fundingMethod ?? undefined;
}

/**
 * Resolves the canonical payout method (UPPER_SNAKE) the transfer must be
 * priced against. The payout is fixed by the claim link, so we prefer the
 * explicit `userData.paymentMethod.payoutMethod` and fall back to mapping the
 * legacy `type` discriminator.
 */
export function resolvePayoutMethod(
  userData?: IUserData | null
): string | undefined {
  const pm = userData?.paymentMethod;
  if (!pm) return undefined;
  if (pm.payoutMethod) return pm.payoutMethod;
  switch (pm.type) {
    case "bank_account":
    case "BANK_ACCOUNT":
      return "BANK_ACCOUNT";
    case "cash":
    case "CASH_PICKUP":
      return "CASH_PICKUP";
    case "card":
    case "PUSH_TO_CARD":
      return "PUSH_TO_CARD";
    default:
      return undefined;
  }
}

/**
 * Resolves the canonical payer code the transfer is locked to (e.g. `ELEKTRA`
 * for cash pickup, `BBVA` for a bank account), or `undefined` when the claim
 * does not pin a specific payer.
 */
export function resolvePayerCode(
  userData?: IUserData | null
): string | undefined {
  const pm = userData?.paymentMethod;
  if (!pm) return undefined;
  if (pm.type === "cash") return pm.cash?.payer ?? pm.cash?.provider;
  if (pm.type === "bank_account") {
    return pm.bankAccount?.payer ?? pm.bankAccount?.bankCode;
  }
  return undefined;
}

/**
 * Narrows a pre-quote's options to the capabilities that satisfy the claim's
 * fixed payout method and payer. The claim pins an exact destination (payout +
 * payer), so matching is STRICT: when a payer is pinned and no option matches
 * it, the result is empty — meaning there is no quotable route for that
 * destination (the UI must surface a "route unavailable" message).
 */
export function filterPreQuoteOptions(
  options: IEstimateOption[] | undefined | null,
  payoutMethod: string | undefined,
  payerCode: string | undefined
): IEstimateOption[] {
  let result = options ?? [];
  if (payoutMethod) {
    result = result.filter((o) => o.payoutMethod === payoutMethod);
  }
  if (payerCode) {
    const target = payerCode.toUpperCase();
    result = result.filter((o) => (o.payerCode ?? "").toUpperCase() === target);
  }
  return result;
}

/**
 * Distinct UI funding categories (CARD / ACCOUNT / CASH / OTHER) available
 * across a set of (already filtered) pre-quote options.
 */
export function availableFundingCategories(
  options: IEstimateOption[]
): Set<FundingCategory> {
  return new Set(
    options
      .map((o) => categoryFromFundingMethod(o.fundingMethod))
      .filter((c): c is FundingCategory => Boolean(c))
  );
}

/**
 * Distinct funding methods available across a set of pre-quote options,
 * in catalog order.
 */
export function availableFundingMethods(
  options: IEstimateOption[]
): FundingMethod[] {
  const seen = new Set(
    options.map((o) => (o.fundingMethod ?? "").toUpperCase())
  );
  return FUNDING_METHODS.filter((m) => seen.has(m));
}

/**
 * Funding methods from pre-collapse facets (preferred) or options fallback —
 * same discovery rule as flow-send.
 */
export function availableFundingMethodsFromDiscovery(input: {
  facetFundingMethods?: Array<{ fundingMethod: string }> | null;
  options?: IEstimateOption[] | null;
}): FundingMethod[] {
  const fromFacets = (input.facetFundingMethods ?? []).map(
    (f) => f.fundingMethod
  );
  if (fromFacets.length > 0) {
    const seen = new Set(fromFacets.map((m) => m.toUpperCase()));
    return FUNDING_METHODS.filter((m) => seen.has(m));
  }
  return availableFundingMethods(input.options ?? []);
}

/**
 * Cheapest (lowest sendAmount) option whose funding method matches the given
 * canonical method, from an already-filtered option list.
 */
export function optionForFundingMethod(
  options: IEstimateOption[],
  method: FundingMethod
): IEstimateOption | undefined {
  const upper = method.toUpperCase();
  return options
    .filter((o) => (o.fundingMethod ?? "").toUpperCase() === upper)
    .sort((a, b) => a.sendAmount - b.sendAmount)[0];
}

/**
 * Best customerFee for a funding method: facets (pre-collapse) first, then
 * the cheapest matching option. `0` from the engine is kept; missing → null.
 */
export function bestCustomerFeeForFundingMethod(
  method: FundingMethod,
  facets?: {
    fundingMethods?: Array<{
      fundingMethod: string;
      best?: { customerFee?: number | null };
    }>;
  } | null,
  options?: Array<{
    fundingMethod?: string;
    customerFee?: number | null;
    sendAmount: number;
  }> | null
): number | null {
  const target = method.toUpperCase();
  const fromFacet = facets?.fundingMethods?.find(
    (row) => (row.fundingMethod ?? "").toUpperCase() === target
  )?.best?.customerFee;
  if (fromFacet != null) return fromFacet;
  const fromOption = (options ?? [])
    .filter((o) => (o.fundingMethod ?? "").toUpperCase() === target)
    .sort((a, b) => a.sendAmount - b.sendAmount)[0]?.customerFee;
  return fromOption != null ? fromOption : null;
}

/**
 * Picks the cheapest (lowest `sendAmount`) option whose funding method maps to
 * the given UI category, from an already-filtered option list.
 */
export function optionForCategory(
  options: IEstimateOption[],
  category: FundingCategory
): IEstimateOption | undefined {
  return options
    .filter((o) => categoryFromFundingMethod(o.fundingMethod) === category)
    .sort((a, b) => a.sendAmount - b.sendAmount)[0];
}

/**
 * Worst-case `sendAmount + customerFee` across offerable options, rounded up
 * to cents. Matches the backend payout-claim create gate
 * (`assertTotalDebitCoversEstimate` with `vary=payer`): the sender must commit
 * to a totalDebit that covers every recipient payout choice, not only the
 * cheapest funding rail shown in the UI.
 */
export function maxTotalDebit(
  options: Array<{ sendAmount: number; customerFee?: number | null }>
): number | null {
  if (options.length === 0) return null;
  let max = 0;
  for (const option of options) {
    const total =
      Number(option.sendAmount) + Number(option.customerFee ?? 0);
    if (total > max) max = total;
  }
  return Math.ceil(max * 100 - 1e-9) / 100;
}
